import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';

function BusinessSpending() {
    const [summary, setSummary] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const userEmail = localStorage.getItem('email');

    useEffect(() => {
        if (!token || role !== 'BUSINESS') {
            navigate('/');
            return;
        }
        fetchSpending();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSpending = async () => {
        setLoading(true);
        try {
            const summaryRes = await axios.get(`${API_URL}/api/business/spending`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSummary(summaryRes.data);

            const campaignsRes = await axios.get(`${API_URL}/api/business/spending/campaigns`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCampaigns(campaignsRes.data.campaigns || campaignsRes.data);

        } catch (err) {
            toast.error('Failed to load spending data');
        }
        setLoading(false);
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
    };

    if (loading) {
        return (
            <div className="dashboard-layout" style={{ alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner" style={{ width: '24px', height: '24px', borderTopColor: '#fff' }}></div>
            </div>
        );
    }

    if (!summary) return null;

    return (
        <div className="dashboard-layout">
            {/* SIDEBAR */}
            <aside className="sidebar">
                <Link to="/" className="sidebar-brand">infloir</Link>
                <nav className="sidebar-nav">
                    <Link to="/business-dashboard" className="nav-item">
                        <span>🏢</span> Dashboard
                    </Link>
                    <Link to="/business/spending" className={`nav-item ${location.pathname === '/business/spending' ? 'active' : ''}`}>
                        <span>💰</span> Spending
                    </Link>
                    <Link to="/profile" className="nav-item">
                        <span>👤</span> Profile
                    </Link>
                </nav>
                <div style={{ marginTop: 'auto' }}>
                    <button onClick={handleLogout} className="nav-item" style={{ width: '100%', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer' }}>
                        <span>🚪</span> Log out
                    </button>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <div className="main-content">
                <header className="topbar">
                    <h2 style={{ fontSize: '18px', fontWeight: '500' }}>Spending Overview</h2>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{userEmail}</span>
                </header>

                <main className="page-container page-fade-in">
                    
                    {/* SUMMARY CARDS */}
                    <div className="stats-grid">
                        <div className="card stat-card">
                            <p className="stat-label">Total Budget</p>
                            <h3 className="stat-value">${parseFloat(summary.totalBudget || 0).toFixed(2)}</h3>
                        </div>
                        <div className="card stat-card">
                            <p className="stat-label">Total Spent</p>
                            <h3 className="stat-value" style={{ color: 'var(--error)' }}>
                                ${parseFloat(summary.totalSpent || 0).toFixed(2)}
                            </h3>
                        </div>
                        <div className="card stat-card">
                            <p className="stat-label">Remaining</p>
                            <h3 className="stat-value" style={{ color: 'var(--success)' }}>
                                ${parseFloat(summary.totalRemaining || 0).toFixed(2)}
                            </h3>
                        </div>
                        <div className="card stat-card">
                            <p className="stat-label">Total Campaigns</p>
                            <h3 className="stat-value">{summary.totalCampaigns || 0}</h3>
                        </div>
                    </div>

                    {/* TASK STATS ROW */}
                    <div className="card" style={{ padding: '24px', marginBottom: '32px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '24px' }}>
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '28px', fontWeight: '600', color: 'var(--text)', letterSpacing: '-0.03em' }}>{summary.totalTasks || 0}</p>
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '4px' }}>Total Tasks</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '28px', fontWeight: '600', color: 'var(--success)', letterSpacing: '-0.03em' }}>{summary.completedTasks || 0}</p>
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '4px' }}>Completed</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '28px', fontWeight: '600', color: 'var(--warning)', letterSpacing: '-0.03em' }}>{summary.pendingTasks || 0}</p>
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '4px' }}>Pending</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '28px', fontWeight: '600', color: 'var(--error)', letterSpacing: '-0.03em' }}>{summary.rejectedTasks || 0}</p>
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '4px' }}>Rejected</p>
                        </div>
                    </div>

                    {/* CAMPAIGN BREAKDOWN */}
                    <div style={{ marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '18px', marginBottom: '4px', color: 'var(--text)' }}>Campaign Breakdown</h3>
                        <p style={{ fontSize: '13px' }}>Detailed view of spending per campaign.</p>
                    </div>

                    {campaigns.length === 0 ? (
                        <div className="card" style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                            <p style={{ fontSize: '32px', marginBottom: '12px' }}>📊</p>
                            <p>No campaign data yet. Create campaigns to see spending.</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Campaign</th>
                                        <th>Budget</th>
                                        <th>Spent</th>
                                        <th>Remaining</th>
                                        <th>Tasks</th>
                                        <th style={{ width: '20%' }}>Progress</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaigns.map(c => {
                                        const progress = c.totalTasks > 0 
                                            ? (c.completedTasks / c.totalTasks) * 100 
                                            : 0;
                                        return (
                                            <tr key={c.campaignId}>
                                                <td style={{ fontWeight: '500', color: 'var(--text)' }}>{c.title}</td>
                                                <td>${parseFloat(c.totalBudget || 0).toFixed(2)}</td>
                                                <td style={{ color: 'var(--error)' }}>
                                                    ${parseFloat(c.spent || 0).toFixed(2)}
                                                </td>
                                                <td style={{ color: 'var(--success)' }}>
                                                    ${parseFloat(c.remaining || 0).toFixed(2)}
                                                </td>
                                                <td>
                                                    <span style={{ color: 'var(--text)' }}>{c.completedTasks || 0}</span>
                                                    <span style={{ color: 'var(--text-muted)' }}> / {c.totalTasks || 0}</span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div style={{ flex: 1, height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden' }}>
                                                            <div style={{ 
                                                                height: '100%', 
                                                                width: `${progress}%`, 
                                                                background: 'var(--accent)', 
                                                                transition: 'width 0.3s ease',
                                                                borderRadius: '3px'
                                                            }}></div>
                                                        </div>
                                                        <span style={{ fontSize: '12px', color: 'var(--text-muted)', minWidth: '35px', textAlign: 'right' }}>
                                                            {progress.toFixed(0)}%
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}

export default BusinessSpending;